import { Check, Search, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type UserSearchResult, useUserSearch } from "@/lib/api";

interface MemberSearchComboboxProps {
  /** User IDs to exclude from search results (already members). */
  excludeIds: string[];
  /** Called when a user is selected from the search results. */
  onSelect: (user: UserSearchResult) => void;
  /**
   * Called when the user clicks "Invite a colleague", passing the current
   * search query. Omit to hide the invite affordance entirely (e.g. admin
   * contexts that already have a dedicated invite flow elsewhere).
   */
  onInviteClick?: (currentQuery: string) => void;
  disabled?: boolean;
  /** Trigger button label. Defaults to "Add member...". */
  triggerLabel?: string;
}

export function MemberSearchCombobox({
  excludeIds,
  onSelect,
  onInviteClick,
  disabled,
  triggerLabel = "Add member...",
}: MemberSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the live query into the value driving the search query so each
  // keystroke doesn't fire a request. TanStack Query dedups + caches results
  // and keeps the previous page on screen via placeholderData.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: results = [], isFetching } = useUserSearch(debouncedQuery, excludeIds);
  // Show the spinner while the live query is ahead of the debounced one, or
  // while the request for the current debounced term is in flight.
  const loading =
    query.trim().length >= 2 && (query.trim() !== debouncedQuery.trim() || isFetching);

  const handleSelect = (user: UserSearchResult) => {
    setOpen(false);
    setQuery("");
    setDebouncedQuery("");
    onSelect(user);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="gap-2">
          <Search className="size-4" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or email..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading ? (
              <p className="text-muted-foreground py-4 text-center text-sm">Searching...</p>
            ) : query.trim().length >= 2 && results.length === 0 ? (
              <CommandEmpty>No users found.</CommandEmpty>
            ) : query.trim().length === 1 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                Type at least 2 characters to search
              </p>
            ) : null}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.id}
                    onSelect={() => handleSelect(user)}
                    className="flex items-center gap-2"
                  >
                    <Check className="text-primary size-4 opacity-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {user.full_name || user.email.split("@")[0]}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {onInviteClick && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      onInviteClick(query.trim());
                    }}
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="size-4" />
                    <span className="text-sm">Can&apos;t find them? Invite a colleague</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
